import { useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet";
import { useNavigate, useParams } from "react-router-dom";
import BreadCrumb from "../components/Breadcrumb";
import PaginationComp from "../components/PaginationComp";
import ProductCard from "../components/ProductCard";
import ProductCardSkeleton from "../components/ProductCardSkeleton";
import ProductListCard from "../components/ProductListCard";
import API from "../utils/api";
import { filterVisibleCategories } from "../utils/categoryFilters";
import styles from "./Shop.module.css";
import { FaTh, FaBars, FaTimes } from "react-icons/fa";

const PRODUCTS_PER_PAGE = 9;

const getImageUrl = (imageName) => {
  const baseUrl = API.base.endsWith("/serverstock_api")
    ? API.base
    : `${API.base}/serverstock_api`;
  return imageName && imageName !== "null" && imageName !== "undefined"
    ? `${baseUrl}/upload/${imageName}`
    : `${baseUrl}/upload/placeholder-image.jpg`;
};

export default function Shop() {
  const { slug } = useParams();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({ search: "", availability: "" });
  const [activeCategory, setActiveCategory] = useState(slug || "");
  const [activeCategoryName, setActiveCategoryName] = useState("");
  const [viewMode, setViewMode] = useState("grid");
  const [currentPage, setCurrentPage] = useState(1);
  const [expandedParents, setExpandedParents] = useState({});
  const [childSelection, setChildSelection] = useState({});
  const navigate = useNavigate();

  // --- Sync activeCategory with slug ---
  useEffect(() => {
    setActiveCategory(slug || "");
  }, [slug]);

  // --- Fetch categories ---
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await API.post("/categories.php", { action: "fetch" });
        const payload = Array.isArray(response.data)
          ? response.data
          : response.data?.data || [];
        const visiblePayload = filterVisibleCategories(payload);

        const parents = visiblePayload
          .filter((cat) => !cat.parent_id)
          .map((parent) => {
            const children = visiblePayload
              .filter(
                (child) =>
                  child.parent_id === parent.id && (child.count ?? 0) > 0
              )
              .map((child) => ({ ...child, count: Number(child.count) || 0 }))
              .sort((a, b) => a.categoryName.localeCompare(b.categoryName));
            const parentHasProducts = (Number(parent.count) || 0) > 0;
            const hasChildrenWithProducts = children.length > 0;
            return {
              ...parent,
              count: Number(parent.count) || 0,
              children,
              hasInventory: parentHasProducts || hasChildrenWithProducts,
            };
          })
          .filter((parent) => parent.hasInventory)
          .sort((a, b) => a.categoryName.localeCompare(b.categoryName));

        setCategories(parents);
      } catch (err) {
        console.error("Failed to load categories:", err);
        setCategories([]);
      }
    };
    fetchCategories();
  }, []);

  // --- Fetch products ---
  useEffect(() => {
    const fetchProducts = async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams({
          action: "fetch",
          status: "publish",
        });
        if (activeCategory) params.append("category_slug", activeCategory);

        const res = await fetch(`${API.base}/products.php`, {
          method: "POST",
          headers: { "Content-Type": "application/x-www-form-urlencoded" },
          body: params,
        });

        if (!res.ok) {
          throw new Error("Failed to fetch products");
        }

        const data = await res.json();

        // Handle both paginated and non-paginated responses
        let productList = [];
        if (Array.isArray(data)) {
          productList = data;
        } else if (data.data && Array.isArray(data.data)) {
          productList = data.data;
        } else if (data.pagination && data.data) {
          productList = data.data;
        }

        setProducts(
          productList.map((product) => ({
            ...product,
            productImage: getImageUrl(product.productImage),
            regularPrice: parseFloat(product.regularPrice) || 0,
            salePrice: parseFloat(product.salePrice) || 0,
          }))
        );
        setCurrentPage(1);
      } catch (err) {
        console.error("Failed to load products:", err);
        setProducts([]);
      } finally {
        setLoading(false);
      }
    };
    fetchProducts();
  }, [activeCategory]);

  const handleCategoryClick = (slug = "") => {
    setActiveCategory(slug);
    navigate(slug ? `/category/${slug}` : "/shop");
    setFilters({ search: "", availability: "" });
    setChildSelection({});
    setExpandedParents({});
    setCurrentPage(1);
  };

  const toggleDropdown = (parentId) => {
    setExpandedParents((prev) => ({ ...prev, [parentId]: !prev[parentId] }));
  };

  const handleChildSelect = (parentId, slug) => handleCategoryClick(slug);

  // --- Update active category name ---
  useEffect(() => {
    if (!categories.length) return;
    if (!slug) return setActiveCategoryName("All Products");

    let name = "All Products";
    let parentIdForChild = null;

    for (const parent of categories) {
      if (parent.category_slug === slug) {
        name = parent.categoryName;
        break;
      }
      const child = parent.children.find((c) => c.category_slug === slug);
      if (child) {
        name = child.categoryName;
        parentIdForChild = parent.id;
        break;
      }
    }

    setActiveCategoryName(name);

    if (parentIdForChild) {
      setChildSelection({ [parentIdForChild]: slug });
      setExpandedParents({ [parentIdForChild]: true });
    } else {
      setChildSelection({});
      setExpandedParents({});
    }
  }, [slug, categories]);

  // --- Filter products ---
  const filteredProducts = useMemo(() => {
    return products.filter((product) => {
      const matchesSearch =
        !filters.search ||
        product.productName
          .toLowerCase()
          .includes(filters.search.toLowerCase());
      const matchesAvailability =
        !filters.availability ||
        (product.availability || "")
          .toLowerCase()
          .includes(filters.availability.toLowerCase());
      return matchesSearch && matchesAvailability;
    });
  }, [products, filters]);

  const paginatedProducts = useMemo(() => {
    const start = (currentPage - 1) * PRODUCTS_PER_PAGE;
    return filteredProducts.slice(start, start + PRODUCTS_PER_PAGE);
  }, [filteredProducts, currentPage]);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
    setCurrentPage(1);
  };

  const handleResetFilters = () => {
    setFilters({ search: "", availability: "" });
    setCurrentPage(1);
  };

  return (
    <>
      <Helmet>
        <title>Shop Server, PC & Networking Products | ServerStock Uk</title>
      </Helmet>

      <BreadCrumb currentPage={activeCategoryName} />

      <div className={styles.shopContainer}>
        <div className={styles.shopLayout}>
          {/* Sidebar */}
          <aside className={styles.shopSidebar}>
            <div className={styles.sidebarCard}>
              <h5>Categories</h5>
              <ul className={styles.categoryList}>
                <li>
                  <button
                    className={
                      !activeCategory
                        ? styles.categoryActive
                        : styles.categoryLink
                    }
                    onClick={() => handleCategoryClick("")}
                  >
                    All Products
                  </button>
                </li>
                {categories.slice(0, 5).map((parent) => (
                  <li key={parent.id} className={styles.parentRow}>
                    <div className={styles.parentHead}>
                      <button
                        className={
                          activeCategory === parent.category_slug
                            ? styles.categoryActive
                            : styles.categoryLink
                        }
                        onClick={() =>
                          handleCategoryClick(parent.category_slug)
                        }
                      >
                        {parent.categoryName}
                      </button>
                      {parent.children.length > 0 && (
                        <button
                          type="button"
                          className={`${styles.toggleBtn} ${
                            expandedParents[parent.id] ? styles.toggleOpen : ""
                          }`}
                          onClick={() => toggleDropdown(parent.id)}
                        >
                          <span />
                        </button>
                      )}
                    </div>

                    {parent.children.length > 0 &&
                      expandedParents[parent.id] && (
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "0.5rem",
                          }}
                        >
                          <select
                            className={styles.childDropdown}
                            value={childSelection[parent.id] || ""}
                            onChange={(e) =>
                              handleChildSelect(parent.id, e.target.value)
                            }
                          >
                            <option value="">Choose sub-category</option>
                            {parent.children.map((child) => (
                              <option
                                key={child.id}
                                value={child.category_slug}
                              >
                                {child.categoryName} ({child.count ?? 0})
                              </option>
                            ))}
                          </select>

                          {childSelection[parent.id] && (
                            <FaTimes
                              style={{
                                cursor: "pointer",
                                color: "var(--primary-color)",
                              }}
                              onClick={() => handleCategoryClick("")}
                              title="Clear Filter"
                            />
                          )}
                        </div>
                      )}
                  </li>
                ))}
              </ul>

              {categories.length > 0 && (
                <button
                  className={styles.viewAllBtn}
                  onClick={() => navigate("/category")}
                >
                  View all categories
                </button>
              )}
            </div>

            {/* Filters */}
            <div className={styles.sidebarCard}>
              <h5>Filters</h5>
              <div className={styles.filterGroup}>
                <label>Search</label>
                <input
                  type="text"
                  name="search"
                  value={filters.search}
                  onChange={handleFilterChange}
                  placeholder="Search products..."
                />
              </div>
              <div className={styles.filterGroup}>
                <label>Availability</label>
                <select
                  name="availability"
                  value={filters.availability}
                  onChange={handleFilterChange}
                >
                  <option value="">Any</option>
                  <option value="in stock">In Stock</option>
                  <option value="out of stock">Out of Stock</option>
                </select>
              </div>
              <div className={styles.filterActions}>
                <button onClick={handleResetFilters}>Reset</button>
              </div>
            </div>
          </aside>

          {/* Main */}
          <main className={styles.shopMain}>
            <div className={styles.shopHeader}>
              <h2>{activeCategoryName}</h2>
              <div className={styles.headerControls}>
                <div className={styles.viewToggle}>
                  <button
                    className={viewMode === "grid" ? styles.activeView : ""}
                    onClick={() => setViewMode("grid")}
                  >
                    <FaTh />
                  </button>
                  <button
                    className={viewMode === "list" ? styles.activeView : ""}
                    onClick={() => setViewMode("list")}
                  >
                    <FaBars />
                  </button>
                </div>
              </div>
            </div>

            {loading ? (
              <div className={styles.skeletonWrapper}>
                <ProductCardSkeleton count={PRODUCTS_PER_PAGE} />
              </div>
            ) : filteredProducts.length === 0 ? (
              <div className={styles.noProducts}>No products found.</div>
            ) : (
              <>
                <div
                  className={
                    viewMode === "grid"
                      ? styles.productGrid
                      : styles.productList
                  }
                >
                  {paginatedProducts.map((product) =>
                    viewMode === "grid" ? (
                      <ProductCard key={product.id} product={product} />
                    ) : (
                      <ProductListCard key={product.id} product={product} />
                    )
                  )}
                </div>

                <div className={styles.paginationGroup}>
                  <p>
                    Showing {filteredProducts.length} product
                    {filteredProducts.length !== 1 ? "s" : ""}
                  </p>
                  <PaginationComp
                    total={filteredProducts.length}
                    perPage={PRODUCTS_PER_PAGE}
                    currentPage={currentPage}
                    onPageChange={setCurrentPage}
                  />
                </div>
              </>
            )}
          </main>
        </div>
      </div>
    </>
  );
}
